"use client";
import { useState, useEffect } from "react";
import useIsMobile from "@app/hooks/useIsMobile";
import { useTranslation } from "@app/useTranslation";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import styles from "./styles.module.scss";
import StyledIcon from "@generic-components/StyledIcon";
import Sidebar from "@generic-components/Layout/Sidenav";
import { EventSourcePolyfill } from "event-source-polyfill";
// import LoginPageComponent from "@page-components/Login";
const LoginPageComponent = dynamic(() => import("@page-components/Login"), {
  ssr: false,
});
import { useDispatch, useSelector } from "react-redux";
import {
  getIsLoggedIn,
  setLoggedIn,
  setProfilePicture,
  getUserDetail,
  getRole,
  removeUserDetails,
  getProfilePicture,
  setUserVerificationStatus,
  setUserDetails,
} from "@app/lib/features/login/loginSlice";
import { setModalState } from "@app/lib/features/modalState/modalSlice";
import { deleteCookie, getCookie } from "@app/utils/cookie";
import { hideLoader, showLoader } from "@app/lib/features/loader/loaderSlice";
import { COOKIE_NAME } from "@app/components/config";
import NotificationModal from "../../NotificationModal";
import axiosInstance from "public/middleware/axios-wrapper";
import { APIURL } from "@app/config/apiUrl";
import dummyImage1 from "public/background/profile.png";
import { resetStore } from "@app/lib/store";
import dynamic from "next/dynamic";
import { DOMAIN } from "@app/config/environment";
import { getNotFound } from "@app/lib/features/not-found/notFoundSlice";
import { setNotificationData } from "@app/lib/features/notification/notificationSlice";
import {
  resetFilter,
  resetLocationInput,
} from "@app/lib/features/buyFilterPayload/buyFilterPayload";
import { pushGaEvents } from "@app/utils/common";

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentPath = usePathname();
  const searchParams = useSearchParams();
  const isLoggedIn = useSelector(getIsLoggedIn) || false;
  const userRole = useSelector(getRole);
  const userData = useSelector(getUserDetail);
  const profilePicture = useSelector(getProfilePicture);
  const route = useRouter();
  const isMobile = useIsMobile();
  const pathname: string = usePathname();
  const isNotFound = useSelector((state: any) => getNotFound(state));
  const showProperties = !!searchParams?.get?.("showProperties");
  const isPropertyDetailsPage1: boolean = pathname.includes("/buy");
  const isPropertyDetailsPage: boolean =
    pathname.includes("/property-details") ||
    pathname.includes("/profile") ||
    pathname.includes("/my-messages") ||
    pathname.includes("/sell/my-properties") ||
    pathname.includes("/sell/dashboard") ||
    pathname.includes("/browse") ||
    pathname.includes("/buy") ||
    pathname.includes("/upload-documents") ||
    pathname.includes("/my-favourites") ||
    pathname.includes("/sell/pending-approval") ||
    pathname.includes("/maintenance") ||
    isNotFound;
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationsData, setNotificationsData] = useState({
    totalUnreadCount: 0,
    enquiryResponse: [],
    messageResponse: [],
  });
  const [headerLogoColor, setHeaderLogoColor] = useState("white");
  const [headerBackgroundColor, setHeaderBackgroundColor] =
    useState("transparent"); // initial header color
  const [scrollPosition, setScrollPosition] = useState(0); // initial scroll position
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [count, setCount] = useState<any>();
  const [retry, setRetry] = useState(0);
  const [dummyImage, setDummyImage] = useState(dummyImage1.src);
  const handleScroll = () => {
    const currentScrollPosition = window.scrollY;
    setScrollPosition(currentScrollPosition);

    const opacityDivisor = window.innerWidth < 768 ? 10 : 20;

    // calculate the opacity of the header color based on the scroll position
    const opacity = Math.min(1, currentScrollPosition / opacityDivisor); // adjust the value to control the transition speed

    // update header background color and text color based on scroll position
    if (currentScrollPosition > 0) {
      setHeaderBackgroundColor(`rgba(255, 255, 255, ${opacity})`);
      // white background with opacity
      setHeaderLogoColor(`rgba(0, 51, 62, ${opacity})`); // black text with opacity
    } else {
      setHeaderBackgroundColor("transparent"); // reset background color to none
      setHeaderLogoColor("white");
    }
  };
  useEffect(() => {
    setDummyImage(generateImage(userData?.userDetails?.fullName));
  }, [userData]);
  useEffect(() => {
    const handleClick = (e: any) => {
      if (
        e.target.classList.contains("dnt") ||
        e?.target?.localName === "svg" ||
        e?.target?.localName === "path"
      ) {
      } else {
        setShowProfileDropdown(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("click", (e) => handleClick(e));
    return () => {
      document.addEventListener("click", (e) => handleClick(e));
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    dispatch(setModalState(showProfileDropdown));
  }, [showProfileDropdown]);

  const getProfile = async () => {
    try {
      const url = `/${APIURL.USER}/${APIURL.PROFILE}`;
      const res: any = await axiosInstance.get(url);
      if (res?.success) {
        dispatch(setUserDetails(res.data));
        dispatch(setUserVerificationStatus(res.data?.profileStatus));
        dispatch(setProfilePicture(res.data?.profileUrl));
      }
    } catch (err: any) {
      console.error("error in getting profile", err?.data?.message);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      getProfile();
    }
  }, [isLoggedIn]);

  const toggleSidenav = () => {
    setIsOpen(!isOpen);
    setShowModal(false);
  };

  const toggleNotificationsModal = () => {
    setShowNotificationModal((prevState) => !prevState);
  };

  const getNotificationData = async () => {
    try {
      const url = `/${APIURL.ENQUIRIES}/${APIURL.GET_UNREAD}`;
      const res = await axiosInstance.get(url, {
        hideLoader: true,
        hideError: true,
      });

      if (res.success && res.data) {
        setNotificationsData(res.data);
        setCount(res.data?.totalUnreadCount);
      }
    } catch (err: any) {
      console.error("error fetching notifications data");
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      getNotificationData();
    }
  }, [showNotificationModal, isLoggedIn, pathname]);

  useEffect(() => {
    if (isLoggedIn) {
      let connectionEstablished = false;
      const token = getCookie(COOKIE_NAME.token) || "";
      const refreshToken = getCookie(COOKIE_NAME.refreshToken) || "";

      const eventSource = new EventSourcePolyfill(
        `${DOMAIN}/${APIURL.ENQUIRIES}/${APIURL.GET_EVENT}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Refresh-Token": refreshToken,
          },
        }
      );

      const timer = setTimeout(() => {
        if (!connectionEstablished) {
          eventSource.close();
          setRetry((prev) => prev + 1);
        }
      }, 3000);

      eventSource.onmessage = (event: any) => {
        connectionEstablished = true;
        clearTimeout(timer);
        const newCount = JSON.parse(event.data);
        if (
          typeof newCount?.totalUnreadCount === "number" ||
          typeof newCount?.initialUnreadCount === "number"
        ) {
          setCount(
            Number(newCount.totalUnreadCount || newCount.initialUnreadCount) ||
              0
          );
        }
      };

      eventSource.onerror = (error: any) => {
        clearTimeout(timer);
        console.error("EventSource failed:", error);
        eventSource.close();
        if (retry <= 5) {
          setRetry((prev) => prev + 1);
        }
      };

      return () => {
        clearTimeout(timer);
        eventSource.close();
      };
    }
  }, [isLoggedIn, retry]);

  useEffect(() => {
    dispatch(setNotificationData(count));
  }, [count]);

  const isPropertyVisible = () => {
    return true;
  };

  const isCurrentPath = (providedPath: string) => {
    return currentPath === providedPath;
  };

  function generateImage(firstName: any) {
    const canvas = document.createElement("canvas");
    const ctx: any = canvas.getContext("2d");
    const width = 100;
    const height = 100;
    canvas.width = width;
    canvas.height = height;

    // Generate a random background color
    const backgroundColor = getRandomColor();

    ctx.fillStyle = "rgb(0, 51, 62)";
    ctx.fillRect(0, 0, width, height);

    // Get the first letter of the first name
    const firstLetter = firstName?.charAt(0)?.toUpperCase();

    // Set the font and text color
    ctx.font = "48px Arial";
    ctx.fillStyle = "#fff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Draw the first letter on the canvas
    ctx.fillText(firstLetter, width / 2, height / 2);

    // Return the canvas as a data URL
    return canvas.toDataURL();
  }

  function getRandomColor() {
    const r = Math.floor(Math.random() * 256);
    const g = Math.floor(Math.random() * 256);
    const b = Math.floor(Math.random() * 256);
    return `rgb(${r}, ${g}, ${b})`;
  }

  useEffect(() => {
    dispatch(setModalState(showModal));
  }, [showModal]);

  const handleLogout = () => {
    dispatch(resetStore());
    dispatch(showLoader());
    route.push("/home");
    dispatch(setLoggedIn(false));
    dispatch(setProfilePicture(""));
    dispatch(removeUserDetails());
    setShowProfileDropdown(false);
    sessionStorage.clear();
    localStorage.clear();
    deleteCookie(COOKIE_NAME.refreshToken);
    deleteCookie(COOKIE_NAME.token);
    deleteCookie(COOKIE_NAME.role);
    deleteCookie(COOKIE_NAME.isLoggedIn);
    deleteCookie(COOKIE_NAME.userDetails);

    setShowModal(false);
    if (pathname.toLowerCase() === "/home") {
      setTimeout(() => {
        dispatch(hideLoader());
      }, 500);
    }
  };

  return (
    <>
      {showModal && (
        <LoginPageComponent
          isSignUp={isSignUp}
          view={showModal}
          onClose={() => {
            setShowModal(false);
          }}
        />
      )}

      {showNotificationModal ? (
        <NotificationModal
          isOpen={showNotificationModal}
          data={notificationsData}
          onClose={toggleNotificationsModal}
          count={count}
        />
      ) : null}

      <Sidebar
        isOpen={isOpen}
        toggleSidenav={() => {
          setIsOpen(!isOpen);
        }}
        isLoginOpen={(signup?: any) => {
          if (signup) {
            setIsSignUp(true);
          } else {
            setIsSignUp(false);
          }
          if (isLoggedIn) {
            dispatch(setLoggedIn(false));
          } else {
            setShowModal((prev) => !prev);
            setTimeout(() => setShowModal(true), 0);
          }
          // isLoggedIn ? dispatch(setLoggedIn(false)) : setShowModal(true);
        }}
      />
      <div
        style={{
          backgroundColor: headerBackgroundColor,
          transition: "background-color 0.3s ease-in-out",
        }}
        className={`${isMobile && "p-3 w-full z-10 fixed"}`}
      >
        <div className={"flex items-center justify-between"}>
          <div className="flex flex-row">
            <button
              id="menu"
              aria-label="menu"
              name="menu"
              className="md:hidden"
              onClick={toggleSidenav}
            >
              <StyledIcon
                icon="hamburger"
                inline={true}
                color={isPropertyDetailsPage ? "#00333E" : headerLogoColor}
              />
            </button>
            <div className="pl-2 block md:hidden self-center">
              <span
                className="cursor-pointer"
                onClick={() => {
                  route.push("/");
                }}
              >
                <StyledIcon
                  icon="logo"
                  width={100}
                  height={18}
                  fill={isPropertyDetailsPage ? "#00333E" : headerLogoColor}
                />
              </span>
            </div>
          </div>
          <div className="self-center justify-self-center md:hidden">
            {isLoggedIn && !showProperties ? (
              <NotificationIcon
                notificationsData={notificationsData}
                toggleNotificationsModal={toggleNotificationsModal}
                count={count}
                headerLogoColor={headerLogoColor}
                isPropertyDetailsPage={isPropertyDetailsPage}
              />
            ) : null}
          </div>
        </div>
        <div
          style={{
            backgroundColor: headerBackgroundColor,
            color: headerLogoColor,
            transition:
              "background-color 0.3s ease-in-out, color 0.3s ease-in-out",
          }}
          className={`hidden md:block main-heading border-[#e0e0e0] border-b-1 border-solid ${
            !isPropertyDetailsPage && "fixed"
          } ${isPropertyDetailsPage1 && "fixed"} w-full px-4 py-0`}
        >
          <div
            className={`grid grid-cols-10 gap-4 justify-between 
              ${isPropertyDetailsPage && styles.colorText}
            ${styles.main}`}
            // style={{
            //   borderBottom:
            //     "1px solid " +
            //     (isPropertyDetailsPage ? "#00333E" : headerLogoColor),
            // }}
          >
            <div className="col-span-3 p-3 pl-5 place-content-left">
              <span
                className="cursor-pointer w-10 grid mt-2"
                onClick={() => {
                  route.push("/");
                }}
              >
                <StyledIcon
                  icon="logo"
                  width={isMobile ? 100 : 137}
                  fill={isPropertyDetailsPage ? "#00333E" : headerLogoColor}
                />
              </span>
            </div>
            <div className="col-span-4 py-0 grid place-content-evenly text-base">
              {isPropertyVisible() && !isMobile && (
                <div className="flex flex-auto gap-0 justify-between header-font-mapper text-center">
                  {
                    <div
                      className={`cursor-pointer hover:underline px-5 pt-6 ${
                        isCurrentPath("/sell") && "border-b-4"
                      } ${styles.main}`}
                      onClick={() => {
                        route.push("/sell");
                      }}
                      style={{
                        borderColor: isPropertyDetailsPage
                          ? "#00333E"
                          : headerLogoColor,
                      }}
                    >
                      {t("header.sell")}
                    </div>
                  }
                  <div
                    className={`cursor-pointer hover:underline px-5 pt-6 ${
                      isCurrentPath("/buy") && "border-b-4"
                    }`}
                    onClick={() => {
                      route.push("/buy");
                    }}
                    style={{
                      borderColor: isPropertyDetailsPage
                        ? "#00333E"
                        : headerLogoColor,
                    }}
                  >
                    {t("header.buy")}
                  </div>
                  <div
                    className={`cursor-pointer hover:underline px-5 pt-6 ${
                      isCurrentPath("/contact-us") && "border-b-4"
                    }`}
                    onClick={() => {
                      route.push("/contact-us");
                    }}
                    style={{
                      borderColor: isPropertyDetailsPage
                        ? "#00333E"
                        : headerLogoColor,
                    }}
                  >
                    {t("header.contact")}
                  </div>
                </div>
              )}
            </div>
            <div
              className={`col-span-3 p-3 pr-0 sm:pr-5 grid place-content-end justify-items-end ${styles.alignItem}`}
            >
              <span className="absolute cursor-pointer dnt flex flex-row">
                {isLoggedIn ? (
                  <NotificationIcon
                    notificationsData={notificationsData}
                    toggleNotificationsModal={toggleNotificationsModal}
                    count={count}
                    headerLogoColor={headerLogoColor}
                    isPropertyDetailsPage={isPropertyDetailsPage}
                  />
                ) : null}
                {isLoggedIn ? (
                  <span
                    onClick={() => {
                      setShowProfileDropdown(true);
                    }}
                    className={`pl-2 dnt ${styles.profileSection}`}
                  >
                    <div
                      className="rounded-3xl border-2 dnt"
                      style={{
                        borderColor: isPropertyDetailsPage
                          ? "#00333E"
                          : headerLogoColor,
                      }}
                    >
                      <div className="dnt">
                        <StyledIcon
                          icon="profileHeader"
                          fill={
                            isPropertyDetailsPage ? "#00333E" : headerLogoColor
                          }
                          color={
                            isPropertyDetailsPage ? "#00333E" : headerLogoColor
                          }
                          inline={true}
                        />
                      </div>
                      <div className=" flex absolute pl-5 pr-3 justify-between top-2.5 right-0 dnt">
                        <div className="dnt text-transparent">&nbsp;</div>
                        <img
                          src={profilePicture ? profilePicture : dummyImage}
                          alt="Profile Icon"
                          className={`${styles.profileIcon} dnt`}
                        />
                      </div>
                    </div>
                  </span>
                ) : (
                  <span
                    onClick={() => {
                      setShowProfileDropdown(true);
                    }}
                    className="flex dnt items-center"
                  >
                    <div
                      className="rounded-3xl dnt border-2"
                      style={{
                        borderColor: isPropertyDetailsPage
                          ? "#00333E"
                          : headerLogoColor,
                      }}
                    >
                      <StyledIcon
                        icon="profileHeader"
                        fill={
                          isPropertyDetailsPage ? "#00333E" : headerLogoColor
                        }
                        color={
                          isPropertyDetailsPage ? "#00333E" : headerLogoColor
                        }
                        inline={true}
                      />
                    </div>
                  </span>
                )}
              </span>
              {showProfileDropdown && (
                <div className="absolute bg-white text-black shadow-md p-2 rounded-xl w-40 top-14 right-7">
                  {isLoggedIn ? (
                    <ul>
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          route.push("/profile");
                          dispatch(resetFilter());
                          dispatch(resetLocationInput());
                          setShowProfileDropdown(false);
                        }}
                      >
                        {t("Profile")}
                      </li>
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          route.push("/my-messages");
                          dispatch(resetFilter());
                          dispatch(resetLocationInput());
                          setShowProfileDropdown(false);
                        }}
                      >
                        {t("My Messages")}
                      </li>
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          route.push("/my-favourites");
                          dispatch(resetFilter());
                          dispatch(resetLocationInput());
                          setShowProfileDropdown(false);
                        }}
                      >
                        {t("My Favourites")}
                      </li>
                      {userRole === "seller" && (
                        <li
                          className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                          onClick={() => {
                            route.push("/sell/my-properties");
                            setShowProfileDropdown(false);
                          }}
                        >
                          {t("My Properties")}
                        </li>
                      )}
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          // Add logout functionality here
                          handleLogout();
                        }}
                      >
                        {t("Logout")}
                      </li>
                    </ul>
                  ) : (
                    <ul>
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          pushGaEvents({
                            event: "sign_in",
                            section: "sell",
                            user_id: userData?.userDetails?.userId,
                          });
                          setShowModal(true);
                          setShowProfileDropdown(false);
                          setIsSignUp(false);
                        }}
                      >
                        {"Log In"}
                      </li>
                      <li
                        className="cursor-pointer hover:bg-gray-100 py-2.5 font-semibold px-1"
                        onClick={() => {
                          pushGaEvents({
                            event: "sign_up",
                            section: "sell",
                            user_id: userData?.userDetails?.userId,
                          });
                          setIsSignUp(true);
                          setShowModal(true);
                          setShowProfileDropdown(false);
                        }}
                      >
                        {"Sign Up"}
                      </li>
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const NotificationIcon = ({
  count,
  notificationsData,
  toggleNotificationsModal,
  isPropertyDetailsPage,
  headerLogoColor,
}: any) => {
  const newCount = count;
  return (
    <span
      className="md:pr-3 justify-center self-center"
      onClick={toggleNotificationsModal}
    >
      <StyledIcon
        icon={newCount ? "notificationUnread" : "notification"}
        inline={true}
        stroke={isPropertyDetailsPage ? "#00333E" : headerLogoColor}
        fill={isPropertyDetailsPage ? "#00333E" : headerLogoColor}
      />
      {/* {newCount ? (
        <span
          style={{
            ...(newCount > 9 && {
              right: "15px",
            }),
          }}
          className={styles.notificationCount}
        >
          {newCount > 9 ? `9+` : newCount}
        </span>
      ) : null} */}
    </span>
  );
};
export default Header;
